import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TYPE_META, initialsFor } from './appTypes';
import { appSearch } from './appDetail';
import itcLogoMark from '../pages/logo.png';
import './AppGrid.css';

/** Number of avatar tints in the rotation; keeps the orange tint sparse. */
const TINT_CYCLE = 5;

/** Cards past this position all share the last stagger delay. */
const MAX_STAGGERED_CARDS = 12;

/*
 * The card is a real link to `?app=<navLink>` rather than a click handler, so
 * middle-click and open-in-new-tab work, and the history entry it pushes is what
 * makes browser-back close the detail modal.
 */
function AppCard({ app, index }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(app.appLogo) && !logoFailed;
  const typeKeys = app.typeKeys || [];

  return (
    <Link
      to={{ search: appSearch(app.navLink) }}
      className="itcCard"
      aria-label={app.name}
      style={{ animationDelay: `${Math.min(index, MAX_STAGGERED_CARDS) * 40}ms` }}
    >
      <span className="itcCardAvatar" data-tint={index % TINT_CYCLE}>
        {showLogo ? (
          <img src={app.appLogo} alt="" onError={() => setLogoFailed(true)} />
        ) : (
          initialsFor(app.name)
        )}
      </span>

      <span className="itcCardName" title={app.name}>
        {app.name}
      </span>

      <span className="itcCardVersion">v{app.versionNo}</span>

      {typeKeys.length > 0 && (
        <span className="itcCardTypes">
          {typeKeys.map((type) => (
            <span
              key={type}
              className="itcCardTypeDot"
              data-type={type}
              title={TYPE_META[type].label}
            />
          ))}
        </span>
      )}
    </Link>
  );
}

AppCard.propTypes = {
  app: PropTypes.shape({
    name: PropTypes.string.isRequired,
    navLink: PropTypes.string.isRequired,
    appLogo: PropTypes.string,
    versionNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    typeKeys: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(TYPE_META))),
  }).isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * The card grid for a store page. Purely presentational: `apps` arrives already
 * scoped and filtered, and an app only shows type dots if the caller decorated
 * it with `typeKeys` (Home does; the per-type pages don't).
 */
export default function AppGrid({ apps, searchQuery = '' }) {
  // The empty state is specified as the no-search-matches state, so it stays out
  // of the way when a list is simply empty and nothing was searched for.
  if (apps.length === 0) {
    if (!searchQuery) return null;

    return (
      <div className="itcEmpty">
        <img className="itcEmptyMark" src={itcLogoMark} alt="" />
        <p className="itcEmptyTitle">No companies match &quot;{searchQuery}&quot;</p>
        <p className="itcEmptyHint">Try a different name or clear the search.</p>
      </div>
    );
  }

  return (
    <div className="itcGrid">
      {apps.map((app, index) => (
        <AppCard key={app.key} app={app} index={index} />
      ))}
    </div>
  );
}

AppGrid.propTypes = {
  apps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      navLink: PropTypes.string.isRequired,
    })
  ).isRequired,
  searchQuery: PropTypes.string,
};
